import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Box, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Trash2Icon } from 'lucide-react';
import apiClient from '../../Services/ApiClient';
import { useSnackbar } from '../../Context/SnackbarProvider';
import { TealSwitch } from '../../Styles/AppStyles';
import {
  FORM,
  FormShell,
  FormBody,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormRow,
  FormMessage,
  FormTextField,
  FormSubmitButton,
  FormCancelButton,
  PhoneNumberField,
} from '../../Components/Form';
import { CustomerAccountDetail as Detail, P21CustomerMatch } from '../../Types/customerAccounts';
import P21CustomerPicker from './P21CustomerPicker';
import { KIND_LABEL, formatDateTime } from './labels';

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 32px;
  border-top: 1px solid ${FORM.border};
  background: ${FORM.surface};

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const LinkList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LinkItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid ${FORM.border};
  border-radius: ${FORM.fieldRadius};
  font-size: 0.875rem;
  color: ${FORM.text};
  > div { min-width: 0; }
  small { display: block; color: ${FORM.textMuted}; font-size: ${FORM.hintSize}; margin-top: 2px; }
`;

const Empty = styled.p`
  margin: 0;
  color: ${FORM.textMuted};
  font-size: 0.875rem;
`;

const History = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8125rem;
  color: ${FORM.text};
  max-height: 220px;
  overflow-y: auto;
  li { display: flex; gap: 12px; flex-wrap: wrap; }
  time { color: ${FORM.textMuted}; font-variant-numeric: tabular-nums; min-width: 160px; }
`;

const ACTION_LABEL: Record<string, string> = {
  account_created: 'Account created',
  phone_changed: 'Phone changed to',
  activated: 'Activated',
  deactivated: 'Deactivated',
  customer_linked: 'Linked P21 customer',
  customer_unlinked: 'Unlinked P21 customer',
};

/**
 * One account: who it is, whether it can sign in, which P21 customers it sees, and
 * every staff change with who made it. Changes apply on the person's next request.
 */
const CustomerAccountDetail: React.FC<{ accountId: number; onChanged: () => void; onClose: () => void }> = ({
  accountId,
  onChanged,
  onClose,
}) => {
  const { showSnackbar } = useSnackbar();
  const [account, setAccount] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [newCustomer, setNewCustomer] = useState<P21CustomerMatch | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient(`/api/customer-accounts/${accountId}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Could not load the account');
      setAccount(body);
      setPhone(body.phoneE164 || '');
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Something went wrong!');
    } finally {
      setLoading(false);
    }
  }, [accountId, showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  /** Runs one change; on success shows the new account state and refreshes the list. */
  const change = async (request: () => Promise<Response>, success: string) => {
    setBusy(true);
    try {
      const response = await request();
      const body = await response.json();
      if (!response.ok) {
        showSnackbar(body.error || 'The change was not saved', 'error', 'Not saved');
        return false;
      }
      const updated = body.account ?? body;
      setAccount(updated);
      setPhone(updated.phoneE164 || '');
      showSnackbar(success, 'success', 'Saved');
      onChanged();
      return true;
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Something went wrong!');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const patch = (body: object) =>
    apiClient(`/api/customer-accounts/${accountId}`, { method: 'PATCH', body: JSON.stringify(body) });

  if (loading && !account) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} sx={{ color: '#0098aa' }} />
      </Box>
    );
  }
  if (!account) {
    return (
      <FormShell $embedded>
        <FormBody><Empty>Account not found.</Empty></FormBody>
        <Footer><FormCancelButton type="button" onClick={onClose}>Close</FormCancelButton></Footer>
      </FormShell>
    );
  }

  const isWebsite = account.kind === 'website';
  const isWorker = account.kind === 'worker';
  const who = account.email ?? account.workerName ?? (account.aimsWorkerId ? `User ${account.aimsWorkerId}` : '');

  return (
    <FormShell $embedded>
      <FormBody>
        <FormSection>
          <SectionTitle>Account</SectionTitle>
          <SectionDescription>
            {isWebsite && 'Signs in with an abatix.com email and password.'}
            {isWorker && 'An AIMS user. Signs in with their RFID or an SMS code.'}
            {!isWebsite && !isWorker && 'Signs in with an SMS code sent to the mobile number below.'}
          </SectionDescription>
          <FormGrid>
            <FormTextField label="Type" value={KIND_LABEL[account.kind]} disabled />
            {isWebsite && <FormTextField label="Website role" value={account.websiteRole ?? 'No role'} disabled />}
            {who ? <FormTextField label={isWebsite ? 'Email' : 'AIMS user'} value={who} disabled /> : null}
            <FormTextField label="Account" value={`#${account.accountId}`} disabled />
            <FormTextField label="Created" value={formatDateTime(account.createdAt)} disabled />
          </FormGrid>
        </FormSection>

        <FormSection>
          <SectionTitle>Access</SectionTitle>
          <SwitchRow>
            <SectionDescription style={{ margin: 0 }}>
              Can sign in. Turning this off blocks the app on the person's next request, even if they are signed in.
            </SectionDescription>
            <TealSwitch
              checked={account.isActive}
              disabled={busy}
              inputProps={{ 'aria-label': 'Can sign in' }}
              onChange={() =>
                account.isActive ? setConfirmDeactivate(true) : change(() => patch({ isActive: true }), 'Account activated')
              }
            />
          </SwitchRow>
          {confirmDeactivate && (
            <FormMessage $type="error">
              Deactivate this account? The person can't use the AIMS app until it's turned back on.
              <FormRow style={{ marginTop: 12 }}>
                <FormCancelButton type="button" onClick={() => setConfirmDeactivate(false)}>Cancel</FormCancelButton>
                <FormSubmitButton
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    await change(() => patch({ isActive: false }), 'Account deactivated');
                    setConfirmDeactivate(false);
                  }}
                >
                  Deactivate
                </FormSubmitButton>
              </FormRow>
            </FormMessage>
          )}
        </FormSection>

        {!isWebsite && (
          <FormSection>
            <SectionTitle>Mobile number</SectionTitle>
            <SectionDescription>
              Sign-in codes go to this number.
              {isWorker && ' Changing it here does not change the AIMS user profile.'}
            </SectionDescription>
            <PhoneNumberField value={phone} onChange={setPhone} onValidationChange={setPhoneError} disabled={busy} />
            <FormRow>
              {account.phoneE164 && (
                <FormCancelButton
                  type="button"
                  disabled={busy}
                  onClick={() => change(() => patch({ phone: null }), 'Mobile number cleared')}
                >
                  Clear (number recycled)
                </FormCancelButton>
              )}
              <FormSubmitButton
                type="button"
                disabled={busy || !!phoneError || !phone || phone === account.phoneE164}
                onClick={() => change(() => patch({ phone }), 'Mobile number saved')}
              >
                Save Number
              </FormSubmitButton>
            </FormRow>
          </FormSection>
        )}

        <FormSection>
          <SectionTitle>P21 customers</SectionTitle>
          <SectionDescription>
            The person sees orders for every customer listed here.
            {isWebsite && ' Links marked abatix.com come from their website account and are refreshed at every website sign-in; add extra ones here (for example national accounts).'}
            {isWorker && ' Managers also see their assigned locations automatically.'}
          </SectionDescription>
          {account.customers.length === 0 ? (
            <Empty>No P21 customers linked.</Empty>
          ) : (
            <LinkList>
              {account.customers.map((link) => (
                <LinkItem key={link.customerId}>
                  <div>
                    <strong>{link.customerId}</strong> · {link.customerName ?? 'Unnamed customer'}
                    <small>
                      {link.source === 'medusa'
                        ? 'From abatix.com'
                        : `Added${link.linkedBy ? ` by ${link.linkedBy}` : ''}${link.linkedAt ? `, ${formatDateTime(link.linkedAt)}` : ''}`}
                    </small>
                  </div>
                  {link.source === 'medusa' ? (
                    <Chip size="small" label="abatix.com" />
                  ) : (
                    <Tooltip title="Unlink: they stop seeing this customer's orders">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Unlink ${link.customerId}`}
                          disabled={busy}
                          onClick={() =>
                            change(
                              () => apiClient(`/api/customer-accounts/${accountId}/links/${link.customerId}`, { method: 'DELETE' }),
                              `Unlinked ${link.customerId}`
                            )
                          }
                        >
                          <Trash2Icon size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </LinkItem>
              ))}
            </LinkList>
          )}
          <FormRow style={{ alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
              <P21CustomerPicker
                value={newCustomer}
                onChange={setNewCustomer}
                exclude={account.customers.map((c) => c.customerId)}
                label="Add a P21 customer"
                disabled={busy}
              />
            </Box>
            <FormSubmitButton
              type="button"
              disabled={busy || !newCustomer}
              onClick={async () => {
                if (!newCustomer) return;
                const ok = await change(
                  () =>
                    apiClient(`/api/customer-accounts/${accountId}/links`, {
                      method: 'POST',
                      body: JSON.stringify({ p21CustomerId: newCustomer.customerId }),
                    }),
                  `Linked ${newCustomer.customerId}`
                );
                if (ok) setNewCustomer(null);
              }}
            >
              Link
            </FormSubmitButton>
          </FormRow>
        </FormSection>

        <FormSection>
          <SectionTitle>History</SectionTitle>
          <SectionDescription>Every change made here, with who made it.</SectionDescription>
          {account.history.length === 0 ? (
            <Empty>No changes recorded yet.</Empty>
          ) : (
            <History>
              {account.history.map((entry, i) => (
                <li key={i}>
                  <time>{formatDateTime(entry.performedAt)}</time>
                  <span>
                    {ACTION_LABEL[entry.action] ?? entry.action}
                    {entry.detail ? ` ${entry.detail}` : ''}
                    {entry.performedBy ? ` · by ${entry.performedBy}` : ''}
                  </span>
                </li>
              ))}
            </History>
          )}
        </FormSection>
      </FormBody>
      <Footer>
        <FormCancelButton type="button" onClick={onClose}>Close</FormCancelButton>
      </Footer>
    </FormShell>
  );
};

export default CustomerAccountDetail;
